import React, { useEffect, useRef, useState } from 'react';
import Gantt from 'frappe-gantt';

const statusProgress = { TODO: 0, PENDING: 0, IN_PROGRESS: 40, IN_REVIEW: 70, DONE: 100, COMPLETED: 100, BLOCKED: 20, OVERDUE: 50 };

export default function GanttChart({ tasks, milestones, project, onTaskClick }) {
  const containerRef = useRef(null);
  const ganttRef = useRef(null);
  const [viewMode, setViewMode] = useState('Week');
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    if (!containerRef.current) return;

    const ganttTasks = [];

    const toDateStr = (ts) => {
      if (!ts) return null;
      try {
        const d = new Date(ts);
        if (isNaN(d.getTime())) return null;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      } catch { return null; }
    };

    (milestones || []).forEach(m => {
      const end = m.dueDate;
      if (!end) return;
      const start = m.startDate || toDateStr(m.createdAt) || end;
      ganttTasks.push({
        id: 'ms' + m.id,
        name: 'MS: ' + m.milestoneName,
        start: start,
        end: end,
        progress: statusProgress[m.status] || 0,
        custom_class: 'bar-milestone'
      });
    });

    (tasks || []).forEach(t => {
      const start = t.startDate || toDateStr(t.createdAt);
      const end = t.dueDate || t.startDate || toDateStr(t.createdAt);
      if (!start || !end) return;
      const cls = t.status === 'DONE' ? 'bar-done'
        : t.status === 'IN_PROGRESS' ? 'bar-inprogress'
        : t.status === 'IN_REVIEW' ? 'bar-review'
        : t.status === 'BLOCKED' ? 'bar-blocked'
        : 'bar-todo';
      ganttTasks.push({
        id: 'task' + t.id,
        name: t.taskCode + ' ' + t.title,
        start: start,
        end: end,
        progress: statusProgress[t.status] || 0,
        custom_class: cls
      });
    });

    setDebugInfo('Tasks: ' + (tasks || []).length + ', Milestones: ' + (milestones || []).length + ', Gantt items: ' + ganttTasks.length);

    if (ganttTasks.length === 0) {
      containerRef.current.innerHTML = '<div style="text-align:center;padding:60px;color:#9ca3af"><div style="font-size:40px;margin-bottom:12px">📊</div><div>No tasks or milestones with dates to display</div></div>';
      return;
    }

    containerRef.current.innerHTML = '';

    try {
      ganttRef.current = new Gantt(containerRef.current, ganttTasks, {
        view_mode: viewMode,
        bar_height: 30,
        bar_corner_radius: 4,
        padding: 18,
        scroll_to: 'start',
        today_button: false,
        infinite_padding: false,
        on_click: (task) => {
          if (task.id.startsWith('task') && onTaskClick) {
            onTaskClick(Number(task.id.replace('task', '')));
          }
        }
      });
      setDebugInfo(prev => prev + ' | Rendered OK');
    } catch (e) {
      console.error('Gantt render error:', e);
      setDebugInfo(prev => prev + ' | ERROR: ' + e.message);
      containerRef.current.innerHTML = '';
    }
  }, [tasks, milestones]);

  const changeView = (mode) => {
    setViewMode(mode);
    if (ganttRef.current) {
      try { ganttRef.current.change_view_mode(mode); } catch (e) { console.error(e); }
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center' }}>
        {['Day', 'Week', 'Month'].map(v => (
          <button key={v} className="btn-ghost" onClick={() => changeView(v)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: 600,
              background: viewMode === v ? '#1a56db' : undefined,
              color: viewMode === v ? '#fff' : undefined,
              borderRadius: 6
            }}>{v}</button>
        ))}
        <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>{debugInfo}</span>
      </div>
      <div ref={containerRef} style={{ overflowX: 'auto', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', minHeight: 200 }} />
      <style>{`
        :root {
          --g-bar-color: #e0e7ff;
          --g-bar-border: #c7d2fe;
          --g-header-background: #f8fafc;
          --g-row-color: #ffffff;
          --g-row-border-color: #e2e8f0;
          --g-tick-color: #f1f5f9;
          --g-tick-color-thick: #e2e8f0;
          --g-border-color: #e2e8f0;
          --g-text-dark: #1e293b;
          --g-text-muted: #64748b;
          --g-text-light: #ffffff;
          --g-progress-color: #c7d2fe;
          --g-arrow-color: #94a3b8;
          --g-weekend-highlight-color: #f8fafc;
          --g-today-highlight: #1a56db;
          --g-popup-actions: #f1f5f9;
        }
        .gantt-container { border: 1px solid #e2e8f0; border-radius: 10px; }
        .bar-milestone .bar { fill: #7c3aed !important; rx: 6; }
        .bar-milestone .bar-label { fill: #fff !important; }
        .bar-milestone .bar-progress { fill: #6d28d9 !important; }
        .bar-todo .bar { fill: #cbd5e1 !important; }
        .bar-todo .bar-label { fill: #475569 !important; }
        .bar-todo .bar-progress { fill: #94a3b8 !important; }
        .bar-inprogress .bar { fill: #3b82f6 !important; }
        .bar-inprogress .bar-label { fill: #fff !important; }
        .bar-inprogress .bar-progress { fill: #2563eb !important; }
        .bar-review .bar { fill: #f59e0b !important; }
        .bar-review .bar-label { fill: #fff !important; }
        .bar-review .bar-progress { fill: #d97706 !important; }
        .bar-done .bar { fill: #10b981 !important; }
        .bar-done .bar-label { fill: #fff !important; }
        .bar-done .bar-progress { fill: #059669 !important; }
        .bar-blocked .bar { fill: #ef4444 !important; }
        .bar-blocked .bar-label { fill: #fff !important; }
        .bar-blocked .bar-progress { fill: #dc2626 !important; }
        .gantt-container .popup-wrapper {
          background: #fff; border-radius: 10px;
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          border: 1px solid #e2e8f0;
        }
        .gantt-container .popup-wrapper .title { color: #0f172a; font-weight: 700; }
        .gantt-container .popup-wrapper .subtitle { color: #334155; }
        .gantt-container .popup-wrapper .details { color: #64748b; }
      `}</style>
      <div style={{ display: 'flex', gap: 16, marginTop: 14, flexWrap: 'wrap' }}>
        {[
          { color: '#7c3aed', label: 'Milestone' },
          { color: '#cbd5e1', label: 'To Do' },
          { color: '#3b82f6', label: 'In Progress' },
          { color: '#f59e0b', label: 'In Review' },
          { color: '#10b981', label: 'Done' },
          { color: '#ef4444', label: 'Blocked' },
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569' }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: l.color, display: 'inline-block' }} />
            {l.label}
          </div>
        ))}
      </div>
    </div>
  );
}
